import { useMemo } from 'react';
import { skuCategoryNameToBundleTypeOrProductLine } from '../../constants';
import { isValidProductLevel } from '../../utils/typeguards';
import { useProductFromProductInventoryByApiName, useProductInventory } from './useProductInventory';
export const useHasApiNameInProductInventory = ({
  apiName
}) => {
  const {
    products,
    loading,
    error
  } = useProductInventory();
  const hasProductInInventory = products === null || products === void 0 ? void 0 : products.some(product => product.productApiName === apiName);
  return {
    hasProductInInventory,
    loading,
    error
  };
};

/**
 * Hook to get all paid hub products from product inventory.
 * A product is considered a paid hub if it's either:
 * 1. A hub product (has skuCategoryName that maps to a known hub/bundle type), or
 * 2. A bundle with bundled products
 */
export const useAllPaidHubsInProductInventory = () => {
  const {
    products,
    loading,
    error
  } = useProductInventory();
  const allPaidHubs = useMemo(() => {
    if (loading) return undefined;
    if (!products) return null;
    return products.filter(product => {
      var _product$bundledProdu, _product$bundledProdu2;
      const isHub = !!(product.skuCategoryName && skuCategoryNameToBundleTypeOrProductLine[product.skuCategoryName]);
      const isBundleWithProducts = product.bundle && ((_product$bundledProdu = (_product$bundledProdu2 = product.bundledProducts) === null || _product$bundledProdu2 === void 0 ? void 0 : _product$bundledProdu2.length) !== null && _product$bundledProdu !== void 0 ? _product$bundledProdu : 0) > 0;
      return isHub || isBundleWithProducts;
    });
  }, [products, loading]);
  return {
    allPaidHubs,
    loading,
    error
  };
};
export const useHasSkuCategoryNameInBundledProductInventory = ({
  skuCategoryName
}) => {
  const {
    products,
    loading,
    error
  } = useProductInventory({
    skip: !skuCategoryName
  });
  const ownedBundleProduct = useMemo(() => {
    if (loading) return undefined;
    if (!products) return null;
    return products.find(product => {
      if (!product.bundle) return false;
      return product.bundledProducts.some(bundledProduct => bundledProduct.skuCategoryName === skuCategoryName);
    });
  }, [products, skuCategoryName, loading]);
  return {
    ownedBundleProduct,
    hasSkuCategoryNameInBundledInventory: loading ? undefined : !!ownedBundleProduct,
    loading,
    error
  };
};
export const useHasSkuCategoryNameInProductInventory = ({
  skuCategoryName
}) => {
  const {
    products,
    loading,
    error
  } = useProductInventory({
    skip: !skuCategoryName
  });
  const hasSkuCategoryNameInInventory = useMemo(() => {
    if (loading) return undefined;
    if (!products) return null;
    return products.some(product => product.skuCategoryName === skuCategoryName);
  }, [products, skuCategoryName, loading]);
  return {
    hasSkuCategoryNameInInventory,
    loading,
    error
  };
};

/**
 * @description
 * Returns whether the specified SKU category name exists in any product inventory,
 * either directly owned or as part of a bundled product.
 *
 * @returns {
 *  hasSkuCategoryNameInAnyInventory: boolean,
 *  loading: boolean,
 *  error: ApolloError | undefined,
 * }
 */
export const useHasSkuCategoryNameInAnyInventory = ({
  skuCategoryName
}) => {
  const directResult = useHasSkuCategoryNameInProductInventory({
    skuCategoryName
  });
  const bundledResult = useHasSkuCategoryNameInBundledProductInventory({
    skuCategoryName
  });
  const loading = directResult.loading || bundledResult.loading;
  const error = directResult.error || bundledResult.error;
  const hasSkuCategoryNameInAnyInventory = useMemo(() => {
    if (loading) return undefined;
    return directResult.hasSkuCategoryNameInInventory || bundledResult.hasSkuCategoryNameInBundledInventory;
  }, [directResult.hasSkuCategoryNameInInventory, bundledResult.hasSkuCategoryNameInBundledInventory, loading]);
  return {
    hasSkuCategoryNameInAnyInventory,
    loading,
    error
  };
};
export const useProductsWithSkuCategoryNameInInventory = ({
  skuCategoryName,
  skip = false
}) => {
  const {
    products,
    loading,
    error
  } = useProductInventory({
    skip: !skuCategoryName || skip
  });
  const productsWithSkuCategoryName = useMemo(() => {
    if (loading) return undefined;
    if (!products) return null;
    return products.filter(product => product.skuCategoryName === skuCategoryName);
  }, [products, skuCategoryName, loading]);
  return {
    productsWithSkuCategoryName,
    loading,
    error
  };
};
export const useHasProductInInventory = ({
  productLevel,
  skuCategoryName
}) => {
  const {
    productsWithSkuCategoryName,
    loading,
    error
  } = useProductsWithSkuCategoryNameInInventory({
    skuCategoryName,
    skip: !productLevel
  });
  const hasProductInInventory = useMemo(() => {
    if (loading) return undefined;
    if (!(productsWithSkuCategoryName !== null && productsWithSkuCategoryName !== void 0 && productsWithSkuCategoryName.length)) return false;
    if (!isValidProductLevel(productLevel)) return false;
    return productsWithSkuCategoryName.some(product => product.productLevel === productLevel);
  }, [productsWithSkuCategoryName, productLevel, loading]);
  return {
    hasProductInInventory,
    loading,
    error
  };
};
export const useQuantityPackInfoFromProductInventory = ({
  productApiName,
  quantityPackApiName
}) => {
  const {
    product,
    loading,
    error
  } = useProductFromProductInventoryByApiName({
    apiName: productApiName,
    skip: !productApiName || !quantityPackApiName
  });
  const quantityPackInfo = useMemo(() => {
    var _product$quantityPack;
    if (loading) return undefined;
    if (!product) return null;
    return (_product$quantityPack = product.quantityPackDetails.find(qp => qp.quantityPackApiName === quantityPackApiName)) !== null && _product$quantityPack !== void 0 ? _product$quantityPack : null;
  }, [product, quantityPackApiName, loading]);
  return {
    quantityPackInfo,
    loading,
    error
  };
};